import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Edit2, Archive, Clock, AlertTriangle, GripVertical, Search, X } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api, extractError } from '../lib/api';
import { fmtDate } from '../lib/date';
import { useConfirm } from '../lib/confirm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DatumFeld from '../components/DatumFeld';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { useAuth } from '../lib/auth';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canCreate = can('projects', 'create');
  const canEdit = can('projects', 'edit');
  const canDelete = can('projects', 'delete');
  const { confirm } = useConfirm();
  const [items, setItems] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Archivierte sind erledigt und wachsen mit jedem Jahr. Sie stehen
  // standardmäßig nicht in der Liste, bleiben aber einen Klick entfernt –
  // gelöscht wird nichts.
  const [archivZeigen, setArchivZeigen] = useState(false);
  const [suche, setSuche] = useState('');
  const [kstFilter, setKstFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get('/projects', { params: { include_archived: true } }),
      api.get('/cost-centers'),
    ]);
    setItems(p.data); setCostCenters(c.data);
  };
  useEffect(() => { load(); }, []);

  // Projekte nach gespeicherter Reihenfolge sortieren (sort_order, dann Name)
  const ordered = useMemo(() => {
    return [...items].sort((a, b) => {
      const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (so !== 0) return so;
      return (a.project_number || a.name || '').localeCompare(b.project_number || b.name || '');
    });
  }, [items]);

  // Geladen wird weiterhin alles – nur so lässt sich sagen, wie viele
  // archivierte es überhaupt gibt, und die Reihenfolge bleibt vollständig.
  const archivAnzahl = useMemo(() => items.filter(p => p.status === 'Archiviert').length, [items]);
  const sichtbare = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return ordered.filter(p => {
      // Wer ausdrücklich nach "Archiviert" filtert, meint das auch – dann
      // hat die ausdrückliche Auswahl Vorrang vor dem Ausblenden-Schalter.
      // Sonst stünde die Liste leer da und niemand wüsste warum.
      if (!archivZeigen && statusFilter !== 'Archiviert' && p.status === 'Archiviert') return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (kstFilter !== 'all' && p.cost_center_id !== kstFilter) return false;
      if (!q) return true;
      // Gesucht wird über alles, wonach im Büro gefragt wird: Nummer, Name,
      // Kunde, Ort. Wer "Müller" im Kopf hat, weiß selten die Projektnummer.
      return [p.project_number, p.name, p.customer, p.location]
        .filter(Boolean)
        .some(feld => String(feld).toLowerCase().includes(q));
    });
  }, [ordered, archivZeigen, suche, kstFilter, statusFilter]);

  // Beim Ziehen wird in der VOLLSTÄNDIGEN Liste umgehängt (siehe
  // handleReorder). Das verschobene Projekt landet genau dort, wo man es
  // hinzieht; ausgeblendete behalten ihre Plätze zueinander.
  const filterAktiv = !!suche.trim() || kstFilter !== 'all' || statusFilter !== 'all';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleReorder = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = ordered.map(p => p.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const newIds = arrayMove(ids, oldIdx, newIdx);
    // Optimistisch anzeigen
    const byId = Object.fromEntries(items.map(p => [p.id, p]));
    setItems(newIds.map((id, i) => ({ ...byId[id], sort_order: i })));
    try {
      await api.post('/projects/reorder', { ordered_ids: newIds });
    } catch (err) { toast.error(extractError(err)); await load(); }
  };

  const openCreate = () => setEditing({ project_number: '', name: '', customer: '', location: '', start_date: new Date().toISOString().slice(0, 10), original_end_date: '', cost_center_id: costCenters[0]?.id || '', description: '', status: 'Aktiv', allow_multiple_per_day: false }) || setEditOpen(true);
  const openEdit = (p) => { setEditing({ ...p }); setEditOpen(true); };

  const save = async () => {
    try {
      if (editing.id) {
        const { id, created_at, extensions, current_end_date, original_end_date, mehrfach_aus_praefix, ...rest } = editing;
        await api.patch(`/projects/${id}`, rest);
        toast.success('Projekt aktualisiert.');
      } else {
        await api.post('/projects', editing);
        toast.success('Projekt angelegt.');
      }
      setEditOpen(false);
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const archive = async (p) => {
    if (!(await confirm({
      title: 'Projekt archivieren?',
      description: `"${p.name}" wird archiviert und in der Plantafel nicht mehr angezeigt.`,
      confirmLabel: 'Archivieren',
    }))) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success('Projekt archiviert.');
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const statusColor = { 'Aktiv': 'bg-emerald-100 text-emerald-900 border-emerald-200', 'Abgeschlossen': 'bg-slate-200 text-slate-700 border-slate-300', 'Pausiert': 'bg-amber-100 text-amber-900 border-amber-300', 'Archiviert': 'bg-slate-100 text-slate-500 border-slate-200' };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
          <CardTitle className="font-display text-lg">
            Projektverwaltung
            <span className="ml-2 text-sm font-normal text-slate-500">
              {sichtbare.length} {sichtbare.length === 1 ? 'Projekt' : 'Projekte'}
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {archivAnzahl > 0 && (
              <Button
                variant={archivZeigen ? 'default' : 'outline'}
                size="sm"
                onClick={() => setArchivZeigen(v => !v)}
                data-testid="toggle-archived-button"
              >
                <Archive size={14} className="mr-1"/>
                {archivZeigen ? 'Archivierte ausblenden' : `Archivierte anzeigen (${archivAnzahl})`}
              </Button>
            )}
            {canCreate && <Button onClick={openCreate} data-testid="create-project-button"><Plus size={14} className="mr-1"/>Projekt anlegen</Button>}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filterleiste */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
              <Input
                value={suche}
                onChange={e => setSuche(e.target.value)}
                placeholder="Nummer, Name, Kunde, Ort…"
                className="h-8 w-64 pl-7 text-sm"
                data-testid="project-search"
              />
            </div>
            <Select value={kstFilter} onValueChange={setKstFilter}>
              <SelectTrigger className="h-8 w-44 text-sm" data-testid="project-filter-kst"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kostenstellen</SelectItem>
                {costCenters.filter(c => !c.deleted).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.short_code} – {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-40 text-sm" data-testid="project-filter-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {['Aktiv', 'Pausiert', 'Abgeschlossen', 'Archiviert'].map(st => (
                  <SelectItem key={st} value={st}>{st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterAktiv && (
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs"
                      onClick={() => { setSuche(''); setKstFilter('all'); setStatusFilter('all'); }}
                      data-testid="project-filter-reset">
                <X size={13} className="mr-1"/>Filter zurücksetzen
              </Button>
            )}
            {canEdit && (
              <span className="text-xs text-slate-500 ml-auto">
                Reihenfolge per Anfasser ziehen – sie gilt auch für die Plantafel.
              </span>
            )}
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder}>
          <SortableContext items={sichtbare.map(p => p.id)} strategy={verticalListSortingStrategy}>
          <Table>
            <TableHeader><TableRow>
              {canEdit && <TableHead className="w-8"/>}
              <TableHead>Projekt-Nr.</TableHead>
              <TableHead>Projekt</TableHead>
              <TableHead>Kunde</TableHead>
              <TableHead>KSt</TableHead>
              <TableHead>Start</TableHead>
              <TableHead>Aktuelles Ende</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sichtbare.map(p => (
                <SortableProjectRow
                  key={p.id}
                  p={p}
                  kst={costCenters.find(c => c.id === p.cost_center_id)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  statusColor={statusColor}
                  onOpen={() => navigate(`/projekte/${p.id}`)}
                  onEdit={() => openEdit(p)}
                  onArchive={() => archive(p)}
                />
              ))}
              {/* Leere Liste erklären statt nur eine leere Tabelle zu zeigen –
                  sonst sieht es nach einem Fehler aus. */}
              {sichtbare.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 9 : 8} className="text-center text-slate-500 py-8">
                    {filterAktiv
                      ? 'Kein Projekt passt zu den Filtern.'
                      : (archivAnzahl > 0 && !archivZeigen
                        ? `Keine laufenden Projekte. ${archivAnzahl} archivierte sind ausgeblendet.`
                        : 'Noch keine Projekte angelegt.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </SortableContext>
          </DndContext>
        </CardContent>
      </Card>

      {editing && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing.id ? 'Projekt bearbeiten' : 'Projekt anlegen'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Projektnummer</Label><Input value={editing.project_number || ''} onChange={e => setEditing({ ...editing, project_number: e.target.value })} placeholder="z.B. 2026-042" data-testid="project-number"/></div>
                <div className="col-span-2"><Label>Projektname</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="project-name"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kunde</Label><Input value={editing.customer} onChange={e => setEditing({ ...editing, customer: e.target.value })} data-testid="project-customer"/></div>
                <div><Label>Ort / Adresse</Label><Input value={editing.location} onChange={e => setEditing({ ...editing, location: e.target.value })} data-testid="project-location"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Startdatum</Label>
                  <DatumFeld value={editing.start_date}
                             onChange={(v) => setEditing({ ...editing, start_date: v })}
                             testId="project-start"/>
                </div>
                {!editing.id ? (
                  <div>
                    <Label>Ursprüngliches Enddatum</Label>
                    <DatumFeld value={editing.original_end_date || ''}
                               onChange={(v) => setEditing({ ...editing, original_end_date: v })}
                               testId="project-end"/>
                  </div>
                ) : (
                  <div><Label>Ursprüngliches Enddatum</Label><Input value={fmtDate(editing.original_end_date)} readOnly className="bg-slate-50"/></div>
                )}
              </div>
              <div>
                <Label>Kostenstelle</Label>
                <Select value={editing.cost_center_id} onValueChange={(v) => setEditing({ ...editing, cost_center_id: v })}>
                  <SelectTrigger data-testid="project-kst"><SelectValue/></SelectTrigger>
                  <SelectContent>{costCenters.filter(c => !c.deleted).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="project-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {['Aktiv', 'Abgeschlossen', 'Archiviert', 'Pausiert'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Beschreibung</Label><Textarea value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} rows={2} data-testid="project-desc"/></div>
              {/* Für Sammelprojekte wie Reparatur oder Wartung: An einem Tag
                  mehrere getrennte Blöcke in der Plantafel, jeder mit eigener
                  Besetzung und eigener Notiz. Hier am Projekt und nicht nur
                  über Nummern-Präfixe in den Einstellungen – ein Präfix, der
                  nicht passt, ist nicht zu erkennen. */}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <label className="flex items-start gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!editing.allow_multiple_per_day}
                    onChange={e => setEditing({ ...editing, allow_multiple_per_day: e.target.checked })}
                    data-testid="project-multi-per-day"
                  />
                  <span>
                    Mehrfach pro Tag planbar
                    <span className="block text-xs font-normal text-slate-500 mt-0.5">
                      Für Sammelprojekte wie Reparatur oder Wartung: In der Plantafel lassen sich
                      dann mehrere getrennte Blöcke an einem Tag anlegen – je Auftrag eigene Leute
                      und eigene Notiz. Bei einer normalen Baustelle ausgeschaltet lassen.
                    </span>
                    {editing.mehrfach_aus_praefix && (
                      <span className="block text-xs font-normal text-sky-700 mt-1">
                        Gilt für dieses Projekt ohnehin schon über die Projektnummer
                        (Einstellungen → Mehrfach planbare Projekte).
                      </span>
                    )}
                  </span>
                </label>
              </div>
              {editing.id && (
                <div className="text-xs text-slate-500 italic">Enddatum ändern über "Verlängern"-Funktion in der Detailansicht.</div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Abbrechen</Button>
              <Button onClick={save} data-testid="project-save-button">Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SortableProjectRow({ p, kst, canEdit, canDelete, statusColor, onOpen, onEdit, onArchive }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id, disabled: !canEdit });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? 'rgb(241 245 249)' : undefined,
  };
  const ext = (p.extensions || []).length;
  const isOverdue = p.status === 'Aktiv' && p.current_end_date < new Date().toISOString().slice(0, 10);
  return (
    <TableRow ref={setNodeRef} style={style} className="cursor-pointer hover:bg-slate-50" onClick={onOpen} data-testid={`project-row-${p.id}`}>
      {canEdit && (
        <TableCell className="w-8 px-1" onClick={e => e.stopPropagation()}>
          <button
            {...listeners}
            {...attributes}
            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-800 p-1"
            title="Ziehen, um die Reihenfolge zu ändern"
            data-testid={`project-drag-${p.id}`}
          >
            <GripVertical size={16} />
          </button>
        </TableCell>
      )}
      <TableCell className="font-mono text-sm font-semibold">{p.project_number || '-'}</TableCell>
      <TableCell className="font-semibold">
        <div className="flex items-center gap-2">
          {p.name}
          {ext > 0 && <Badge variant="outline" className="text-xs"><Clock size={10} className="mr-0.5"/>verlängert</Badge>}
          {isOverdue && <Badge className="bg-rose-700 text-white hover:bg-rose-700 text-xs"><AlertTriangle size={10} className="mr-0.5"/>überfällig</Badge>}
        </div>
      </TableCell>
      <TableCell>{p.customer}</TableCell>
      <TableCell>{kst ? <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: kst.color }}/>{kst.short_code}</span> : '-'}</TableCell>
      <TableCell className="font-mono text-sm">{fmtDate(p.start_date)}</TableCell>
      <TableCell className="font-mono text-sm">{fmtDate(p.current_end_date)}</TableCell>
      <TableCell><Badge variant="outline" className={statusColor[p.status]}>{p.status}</Badge></TableCell>
      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
        {canEdit && <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`edit-project-button-${p.id}`}><Edit2 size={14}/></Button>}
        {canDelete && p.status !== 'Archiviert' && (
          <Button size="sm" variant="ghost" onClick={onArchive} className="text-rose-600" data-testid={`archive-project-button-${p.id}`}><Archive size={14}/></Button>
        )}
        {!canEdit && !canDelete && <span className="text-xs text-slate-400">–</span>}
      </TableCell>
    </TableRow>
  );
}
